import type { IGridifyPaging, IResponseBase } from '@/interfaces';
import type {
	IPreAuditDecideRequest,
	IPreAuditDetail,
	IPreAuditFindingFeedbackResponse,
	IPreAuditListItem,
	IPreAuditPartnerView,
	IPreAuditSummary,
	IPreAuditDocumentLock,
} from '@/interfaces/IPreAudit';
import type { IGridifyQuery } from '@/models/GridifyQuery';
import axios from 'axios';
import { Http, HttpService } from './http';

const http = Http.create();

/**
 * Pré-auditoria do contrato de locação.
 *
 * A máquina (Bilbo) lê contrato + vistoria e publica um checklist de achados; o analista da
 * Almada decide (aprovar, retornar ao parceiro ou cancelar). O parceiro só enxerga o retorno,
 * pela `partner-view`.
 */
export class PreAuditService extends HttpService {
	/**
	 * Fila interna paginada (Gridify). Filtros aceitos no `filter`: status, brokerId, slaStatus,
	 * machineVerdict, decision. A resposta vem como `{ count, data }`, sem `sumarized` — o campo é
	 * preenchido vazio para a tabela genérica não tropeçar.
	 */
	static async list(query: IGridifyQuery | null = null): Promise<IGridifyPaging<IPreAuditListItem>> {
		const response = await http.get<Partial<IGridifyPaging<IPreAuditListItem>>>(`/pre-audits`, {
			params: query,
		});
		return {
			data: response.data.data ?? [],
			count: response.data.count ?? 0,
			sumarized: response.data.sumarized ?? [],
		};
	}

	static async getSummary() {
		const response = await http.get<IResponseBase<IPreAuditSummary>>(`/pre-audits/summary`);
		return response.data.data;
	}

	/** 404 quando o caso nunca entrou em pré-auditoria — ver `isPreAuditNotFound`. */
	static async getByCase(caseId: string) {
		const response = await http.get<IResponseBase<IPreAuditDetail>>(`/cases/${caseId}/pre-audit`);
		return response.data.data;
	}

	/** Visão do parceiro: só o retorno do analista. 404 quando nunca houve pré-auditoria. */
	static async getPartnerView(caseId: string) {
		const response = await http.get<IResponseBase<IPreAuditPartnerView>>(
			`/cases/${caseId}/pre-audit/partner-view`,
		);
		return response.data.data;
	}

	static async decide(caseId: string, payload: IPreAuditDecideRequest) {
		const response = await http.post<IResponseBase<IPreAuditDetail>>(
			`/cases/${caseId}/pre-audit/decide`,
			payload,
		);
		return response.data.data;
	}

	/** Resposta da imobiliária à devolução da tentativa atual (uma por tentativa; reenviar substitui). */
	static async replyAsPartner(caseId: string, text: string) {
		const response = await http.post<IResponseBase<IPreAuditPartnerView>>(
			`/cases/${caseId}/pre-audit/partner-reply`,
			{ text },
		);
		return response.data.data;
	}

	/** "Isso está errado": score no Langfuse (o operador só recebe a confirmação). Não muda o contrato. */
	static async sendFindingFeedback(caseId: string, findingId: string, comment: string) {
		const response = await http.post<IResponseBase<IPreAuditFindingFeedbackResponse>>(
			`/cases/${caseId}/pre-audit/findings/${findingId}/feedback`,
			{ comment },
		);
		return response.data.data;
	}

	/**
	 * Trava dos documentos de locação: fora de "Pendente de documento de locação" só o ADMIN altera
	 * contrato, vistoria, aditivos e T&C do contrato.
	 */
	static async getDocumentLock(caseId: string) {
		const response = await http.get<IResponseBase<IPreAuditDocumentLock>>(`/cases/${caseId}/pre-audit/document-lock`);
		return response.data.data;
	}

	static async reprocess(caseId: string) {
		const response = await http.post<IResponseBase<IPreAuditDetail>>(
			`/cases/${caseId}/pre-audit/reprocess`,
		);
		return response.data.data;
	}

	/**
	 * O 404 de `GET /cases/{id}/pre-audit` é resposta de negócio ("nunca entrou em pré-auditoria"),
	 * não erro — é o que decide se a aba aparece. Qualquer outro erro continua sendo erro.
	 */
	static isPreAuditNotFound(error: unknown) {
		return axios.isAxiosError(error) && error.response?.status === 404;
	}
}

export default PreAuditService;
